<template>
  <v-container>
    <!-- Shared filter surface (matches GroupCounting). PhotoFilter emits
         apply-filters when the operator clicks Ok/Reset; sidecar
         "Only Needs Review" checkbox is QC-specific and re-triggers the
         load on change so operators can toggle it without re-opening
         the modal. -->
    <v-row class="mb-4">
      <v-col cols="9">
        <PhotoFilter :initial-filter="qcFilters" @apply-filters="setFilters($event)"></PhotoFilter>
      </v-col>
      <v-col cols="3" class="d-flex flex-column justify-center">
        <v-checkbox
          v-model="qcFilters.onlyNeedsReview"
          label="Only Needs Review"
          hide-details
          @change="getGroups"
        ></v-checkbox>
        <v-checkbox
          v-model="qcFilters.showQCed"
          label="Include already-reviewed"
          hide-details
          @change="getGroups"
        ></v-checkbox>
      </v-col>
    </v-row>

    <v-progress-circular v-if="isLoading" indeterminate></v-progress-circular>

    <v-row v-if="!isLoading && qcGroups.length === 0">
      <v-col cols="12" class="text-center">
        <h3>No groups available for QC</h3>
      </v-col>
    </v-row>

    <!-- Groups List -->
    <div v-for="group in qcGroups" :key="group.id" class="group-card mb-6">
      <v-card>
        <v-card-title>
          Group: {{ group.response_group_id }} / Question: {{ group.question_id }}
          <v-chip v-if="group.needs_review" color="warning" class="ml-2" small>Needs Review</v-chip>
          <v-spacer></v-spacer>
          <span class="text-caption">Tagged by: {{ group.tagger || 'Unknown' }}</span>
        </v-card-title>

        <v-card-text>
          <!-- Photo Gallery -->
          <div class="photo-gallery">
            <div v-for="photo in group.photos" :key="photo.id" class="photo-item">
              <v-img
                :src="photo.image_url"
                :aspect-ratio="1"
                class="photo-image"
                contain
                @click="openLightbox(photo)"
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <div class="photo-id text-caption text-center">ID: {{ photo.response_id }}</div>
              <div
                v-if="photo.tagger"
                class="photo-tagger text-caption text-center"
              >
                Tagger: {{ photo.tagger }}
              </div>
            </div>
          </div>

          <!-- Current Count Values -->
          <div class="mt-4" v-if="group.count_entries && group.count_entries.length > 0">
            <strong>Current Counts:</strong>
            <v-chip-group column>
              <v-chip v-for="entry in group.count_entries" :key="entry.count_item_id" small color="primary" outlined>
                {{ entry.count_item_name }}: {{ entry.value }}
              </v-chip>
            </v-chip-group>
          </div>

          <!-- Count Editor -->
          <v-expansion-panels class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-header>Edit Counts</v-expansion-panel-header>
              <v-expansion-panel-content>
                <CountPanel
                  :photo="group"
                  :isQc="true"
                  :isGroup="true"
                  @saved="onCountSaved(group)"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </div>

    <!-- Submit Buttons -->
    <v-container v-if="qcGroups.length > 0" class="fixed border--primary">
      <v-btn class="mr-2 float-right" color="success" @click="submitBatch({getNext: true})">
        Mark Reviewed & Get Next Batch
      </v-btn>
      <v-btn class="mr-2 float-right" color="primary" @click="submitBatch({getNext: false})">
        Mark Reviewed & Close
      </v-btn>
    </v-container>

    <!-- Lightbox -->
    <v-dialog v-model="lightboxOpen" max-width="90%">
      <v-card>
        <div v-if="lightboxPhoto" class="lightbox-image-container">
          <image-zoom
            :key="lightboxPhoto.id"
            :regular="lightboxPhoto.image_url"
            :zoom="lightboxPhoto.image_url"
            :zoom-amount="4"
            :click-zoom="true"
            img-class="lightbox-image"
          />
        </div>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="lightboxOpen = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import imageZoom from 'vue-image-zoomer'
import PhotoFilter from '@/components/filters/PhotoFilter'
import {routeFilterSeed} from '@/components/filters/routeSeed'
import CountPanel from '@/components/counts/CountPanel'

export default {
  name: 'GroupCountQC',
  components: { PhotoFilter, CountPanel, imageZoom },
  data() {
    return {
      // PhotoFilter-shaped object + QC-specific sidecar fields.
      // Same skeleton as GroupCounting so the modal populates cleanly;
      // onlyNeedsReview / showQCed / taggers are QC-only and pass
      // through to the quality_control endpoint via fetchQCGroups.
      qcFilters: {
        fromDt: null,
        thruDt: null,
        tags: [],
        projects: [],
        jobs: [],
        onlyUntagged: false,
        includePartiallyTagged: true,
        includeInactive: false,
        respIdSearchFreeForm: null,
        rgidSearchFreeForm: null,
        country: null,
        retailer: null,
        department: null,
        onlyNeedsReview: false,
        showQCed: false,
        taggers: [],
      },
      isLoading: false,
      lightboxOpen: false,
      lightboxPhoto: null
    }
  },
  // Quick-link deep links (routeSeed.js) must seed the filter in created(),
  // BEFORE the filter child mounts: Vue 2 mounts the child before the parent,
  // so seeding in the parent's mounted() lands after the child has snapshotted
  // initialFilter — leaving the Applied-Filters chips blank and dropping the
  // scope on the next Apply. created() runs first, so the child snapshots the
  // seeded filter. Matches BoxTagging's timing.
  created() {
    this.qcFilters = {...this.qcFilters, ...routeFilterSeed(this.$route)}
  },
  mounted() {
    this.getCountItems()
    this.setQCGroups([])
    this.getGroups()
  },
  computed: {
    ...mapState({
      qcGroups: state => state.photoGroups.qcGroups
    })
  },
  methods: {
    ...mapActions(['fetchQCGroups', 'submitQCGroups', 'getCountItems']),
    ...mapMutations(['setMessage', 'setQCGroups']),

    async getGroups() {
      this.isLoading = true
      try {
        await this.fetchQCGroups(this.qcFilters)
      } finally {
        this.isLoading = false
      }
    },
    setFilters(newFilter) {
      // PhotoFilter emits its own shape; merge in so we preserve the
      // QC-specific sidecar fields (onlyNeedsReview, showQCed, taggers).
      this.qcFilters = {
        ...this.qcFilters,
        ...newFilter,
      }
      this.getGroups()
    },
    onCountSaved(group) {
      this.setMessage({ text: `Counts updated for group ${group.response_group_id}`, type: 'success', alert: true })
    },
    async submitBatch(payload) {
      await this.submitQCGroups()
      this.setMessage({ text: 'Groups Marked Reviewed', type: 'success', alert: true })
      if (payload.getNext) {
        this.getGroups()
      }
    },
    openLightbox(photo) {
      this.lightboxPhoto = photo
      this.lightboxOpen = true
    }
  }
}
</script>

<style scoped>
.border--primary {
  border: 1px solid #666;
  border-radius: 4px;
  padding: 10px;
}
.fixed {
  position: fixed !important;
  bottom: 0 !important;
  z-index: 999;
  background: white;
}
.photo-gallery {
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 16px 0;
  overflow-x: auto;
  min-height: 150px;
}
.photo-item {
  flex: 0 0 auto;
  width: 150px;
}
.photo-image {
  cursor: pointer;
  border: 1px solid #ddd;
  border-radius: 4px;
}
.photo-image:hover {
  border-color: var(--v-primary-base);
}
.photo-id {
  margin-top: 4px;
  color: #666;
}
.lightbox-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 80vh;
  overflow: hidden;
}
</style>

<!--
  vue-image-zoomer renders its own <img> inside a child component;
  scoped CSS's [data-v-XXXX] attribute selector doesn't reach it, so
  .lightbox-image rules only bind when this block is UNSCOPED.
-->
<style>
.lightbox-image {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  display: block;
}
</style>
